import React, { useState } from "react";
import { CaretDown } from "@phosphor-icons/react";
import { TYPE_META } from "../../lib/incidents";

const FlameGlyph = ({ color }) => (
  <svg width="13" height="13" viewBox="0 0 24 24" className="shrink-0">
    <path
      d="M12 1.5c.6 3.2-.9 5-2.5 6.8C7.7 10.3 6 12.2 6 15a6 6 0 0 0 12 0c0-2.2-.9-3.9-2-5.5-.4 1-.9 1.7-1.8 2.3.3-3.6-1-8-2.2-10.3z"
      fill={color}
      stroke="#0A0D0C"
      strokeWidth="1.4"
      strokeLinejoin="round"
    />
    <path
      d="M12 20.4a3 3 0 0 1-3-3c0-1.4.8-2.3 1.6-3.2.5.7 1 1.1 1.6 1.4-.1-1.1.2-2.4.8-3.4 1.1 1.4 2 2.8 2 5.2a3 3 0 0 1-3 3z"
      fill="#FFD166"
      opacity="0.92"
    />
  </svg>
);

const CautionGlyph = ({ color = "#22C55E" }) => (
  // Zone-number pill (matches the pill rendered on the map). We keep the
  // component name for backward compatibility with legacy imports.
  <span
    className="shrink-0 rounded-full font-bold"
    style={{
      background: color,
      color: "#fff",
      fontSize: 9,
      lineHeight: 1,
      padding: "2px 6px",
      letterSpacing: "0.02em",
    }}
  >
    A#
  </span>
);

const RoadClosedGlyph = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" className="shrink-0">
    <polygon
      points="7.5,1.5 16.5,1.5 22.5,7.5 22.5,16.5 16.5,22.5 7.5,22.5 1.5,16.5 1.5,7.5"
      fill="#C1121F"
      stroke="#0A0D0C"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
    <rect x="4.5" y="10" width="15" height="4" rx="1" fill="#FFFFFF" />
  </svg>
);

const WsdotClosureGlyph = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" className="shrink-0">
    <rect x="4.2" y="4.2" width="15.6" height="15.6" rx="2" transform="rotate(45 12 12)" fill="#F77F00" stroke="#0A0D0C" strokeWidth="1.5" />
    <rect x="10.9" y="6.5" width="2.2" height="7" rx="1" fill="#0A0D0C" />
    <circle cx="12" cy="16.4" r="1.4" fill="#0A0D0C" />
  </svg>
);

const AircraftGlyph = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" className="shrink-0">
    <path
      d="M12 1.8 L13.6 8.2 L22 12.2 L22 14.4 L13.6 12.2 L13.4 18 L16.2 20.4 L16.2 22.2 L12 21 L7.8 22.2 L7.8 20.4 L10.6 18 L10.4 12.2 L2 14.4 L2 12.2 L10.4 8.2 Z"
      fill="#4CC9F0"
      stroke="#0A0D0C"
      strokeWidth="1.2"
      strokeLinejoin="round"
    />
  </svg>
);

const CameraGlyph = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" className="shrink-0">
    <path d="M9 6.5l1.4-2.5h3.2L15 6.5" fill="#457B9D" stroke="#0A0D0C" strokeWidth="1.4" strokeLinejoin="round" />
    <rect x="2.2" y="6.5" width="19.6" height="13.5" rx="2.5" fill="#457B9D" stroke="#0A0D0C" strokeWidth="1.4" />
    <circle cx="12" cy="13.2" r="4.2" fill="#0A0D0C" />
    <circle cx="12" cy="13.2" r="2.3" fill="#A8DADC" />
  </svg>
);

const Dot = ({ color, ring }) => (
  <span
    className={`w-2.5 h-2.5 rounded-full shrink-0 ${ring ? "border-2 border-white" : "border border-tac-bg"}`}
    style={{ background: color }}
  />
);

const Row = ({ children, label, testId }) => (
  <div className="flex items-center gap-2 text-tac-text" data-testid={testId}>
    {children}
    <span>{label}</span>
  </div>
);

export default function MapLegend({ cameras, perimeters, locations, nifc, viirs, dnr, evacs, roadClosures, wsdotClosures, aircraft, countyName }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="absolute bottom-3 left-3 z-[1000] bg-tac-bg/85 backdrop-blur-xl border border-tac-border rounded-sm text-[10px] font-mono-tac"
      data-testid="map-legend"
    >
      <button
        type="button"
        data-testid="map-legend-toggle"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-2 uppercase tracking-wider text-tac-faint hover:text-tac-text font-bold transition-colors"
      >
        <FlameGlyph color="#F2542D" />
        Legend
        <CaretDown
          size={10}
          weight="bold"
          className={`transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
      <div className="flex flex-col gap-1.5 px-3 pb-2.5">
        {Object.entries(TYPE_META).map(([k, m]) => (
          <Row key={k} label={m.label}>
            <FlameGlyph color={m.color} />
          </Row>
        ))}
        {evacs > 0 && (
          <>
            <Row label="Level 1 · Get Ready" testId="legend-evac-l1">
              <CautionGlyph color="#22C55E" />
            </Row>
            <Row label="Level 2 · Get Set" testId="legend-evac-l2">
              <CautionGlyph color="#FF8F1F" />
            </Row>
            <Row label="Level 3 · Go Now" testId="legend-evac-l3">
              <CautionGlyph color="#E63946" />
            </Row>
          </>
        )}
        {roadClosures > 0 && (
          <Row label="Road closed" testId="legend-road-closed">
            <RoadClosedGlyph />
          </Row>
        )}
        {wsdotClosures > 0 && (
          <Row label="WSDOT closure" testId="legend-wsdot-closure">
            <WsdotClosureGlyph />
          </Row>
        )}
        {aircraft > 0 && (
          <Row label="Fire aviation" testId="legend-aircraft">
            <AircraftGlyph />
          </Row>
        )}
        {cameras > 0 && (
          <Row label="Camera">
            <CameraGlyph />
          </Row>
        )}
        {perimeters > 0 && (
          <div className="flex items-center gap-2 text-tac-text">
            <span className="w-3 h-2 border border-tac-bg shrink-0" style={{ background: "rgba(230,57,70,0.4)", borderColor: "#E63946" }} />
            <span>Fire perimeter</span>
          </div>
        )}
        {locations > 0 && (
          <Row label="Saved location">
            <Dot color="#F2542D" ring />
          </Row>
        )}
        {nifc > 0 && (
          <Row label="NIFC active">
            <Dot color="#FF5630" />
          </Row>
        )}
        {viirs > 0 && (
          <Row label="VIIRS hotspot">
            <Dot color="#FF8A1F" />
          </Row>
        )}
        {dnr > 0 && (
          <Row label="DNR fire">
            <FlameGlyph color="#10B981" />
          </Row>
        )}
        <div className="flex items-center gap-2 text-tac-dim mt-1 pt-1 border-t border-tac-border">
          <span className="w-3 h-2 border border-dashed border-tac-primary shrink-0" />
          <span>{countyName || "County"}</span>
        </div>
      </div>
      )}
    </div>
  );
}
